<template>
  <!-- Dashboard: light compact tiles -->
  <div v-if="variant === 'dashboard'" class="cps-dash-grid">
    <button
      v-for="product in products"
      :key="product.id"
      type="button"
      :class="['cps-dash-tile', product.soon ? 'cps-dash-tile--soon' : '']"
      @click="onCardClick(product)"
    >
      <div
        class="cps-dash-tile-icon"
        :style="{ background: iconBg(product), color: product.accent }"
      >
        <v-icon :color="product.accent" size="26">{{ product.mdiIcon }}</v-icon>
      </div>
      <p class="cps-dash-tile-title">{{ product.name }}</p>
      <p class="cps-dash-tile-meta">{{ product.soon ? 'Coming soon' : 'Get a quote' }}</p>
    </button>
  </div>

  <!-- Catalog: product browser (Britam-inspired layout, Pacis branding) -->
  <div v-else>

    <!-- Loading state -->
    <div v-if="loading" class="cps-grid cps-grid--catalog">
      <article
        v-for="n in 6"
        :key="n"
        class="cps-catalog-card cps-skeleton-card"
      >
        <div class="cps-skeleton cps-skeleton-media"></div>

        <div class="cps-catalog-content">
          <div class="cps-skeleton cps-skeleton-title"></div>

          <div class="cps-skeleton cps-skeleton-text"></div>
          <div
            class="cps-skeleton cps-skeleton-text cps-skeleton-text--short"
          ></div>

          <div class="cps-skeleton-tags">
            <span class="cps-skeleton cps-skeleton-tag"></span>
            <span class="cps-skeleton cps-skeleton-tag"></span>
          </div>

          <div class="cps-skeleton cps-skeleton-button"></div>
        </div>
      </article>
    </div>

    <!-- Empty state -->
    <p
      v-else-if="filteredProducts.length === 0"
      class="cps-empty-filter"
    >
      No products in this category yet.
    </p>

    <!-- Actual products -->
    <div v-else class="cps-grid cps-grid--catalog">
      <article
        v-for="product in filteredProducts"
        :key="product.id"
        :class="catalogCardClass(product)"
        @click="onCardClick(product)"
      >
        <div
          class="cps-catalog-media"
          :style="{ background: mediaGradient(product) }"
        >
          <v-icon color="white" size="40">{{ product.mdiIcon }}</v-icon>

          <span
            :class="[
              'cps-status-pill',
              product.live
                ? 'cps-status-pill--live'
                : 'cps-status-pill--soon'
            ]"
          >
            {{ product.live ? 'Available' : 'Coming soon' }}
          </span>
        </div>

        <div class="cps-catalog-content">
          <h3 class="cps-catalog-name">{{ product.name }}</h3>

          <p class="cps-catalog-desc">
            {{ product.description }}
          </p>

          <div class="cps-catalog-tags">
            <span
              v-for="tag in product.tags"
              :key="tag"
              class="cps-catalog-tag"
            >
              {{ tag }}
            </span>
          </div>

          <button
            v-if="product.live"
            type="button"
            class="cps-catalog-buy"
            @click.stop="go(product)"
          >
            {{ ctaLabel }}
            <v-icon small color="white">mdi-arrow-right</v-icon>
          </button>

          <p
            v-else
            class="cps-catalog-soon-note"
          >
            {{ product.soonNote || 'Available shortly' }}
          </p>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  CUSTOMER_DASHBOARD_PRODUCTS,
  CUSTOMER_PRODUCTS,
} from '@/constants/customerProducts';
import { withReferralQuery } from '@/services/referralApi';

export default {
  name: 'CustomerProductTray',

  props: {
    variant: {
      type: String,
      default: 'catalog',
      validator: (v) => ['dashboard', 'catalog'].includes(v),
    },

    limit: {
      type: Number,
      default: 0,
    },

    category: {
      type: String,
      default: 'all',
    },

    /** Override catalogue list (e.g. guest Get Quote routes). */
    productsOverride: {
      type: Array,
      default: null,
    },

    /** Catalog CTA label — "Buy now" (default) or "Get Quote". */
    ctaLabel: {
      type: String,
      default: 'Buy now',
    },

    loading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters('referral', ['referralCode']),

    sourceList() {
      if (this.productsOverride && this.productsOverride.length) {
        const list = this.productsOverride;

        if (this.limit > 0) {
          return list.slice(0, this.limit);
        }

        return list;
      }

      const list = this.variant === 'dashboard'
        ? CUSTOMER_DASHBOARD_PRODUCTS
        : CUSTOMER_PRODUCTS;

      if (this.limit > 0) {
        return list.slice(0, this.limit);
      }

      return list;
    },

    products() {
      return this.sourceList;
    },

    filteredProducts() {
      if (this.variant === 'dashboard' || this.category === 'all') {
        return this.sourceList;
      }

      return this.sourceList.filter(
        (p) => p.category === this.category,
      );
    },
  },

  methods: {
    iconBg(product) {
      const hex = product.accent || '#184679';
      return `${hex}14`;
    },

    mediaGradient(product) {
      const accent = product.accent || '#184679';
      return `linear-gradient(135deg, ${accent} 0%, #0f2d52 100%)`;
    },

    catalogCardClass(product) {
      const base = ['cps-catalog-card'];

      if (product.live) {
        return [...base, 'cps-catalog-card--clickable'];
      }

      return [...base, 'cps-catalog-card--disabled'];
    },

    onCardClick(product) {
      if (!product.live) {
        if (product.soon) {
          this.$toast.info(
            product.soonNote || 'This product is coming soon.',
            product.name,
          );
        }

        return;
      }

      this.go(product);
    },

    go(product) {
      if (!product.route) return;

      // Keep agentCode in the URL across product journeys so referral attribution does not drop.
      this.$router.push(
        withReferralQuery(product.route, this.referralCode),
      );
    },
  },
};
</script>

<style scoped src="@/assets/css/customer-product-select.css"></style>
